import { Bell, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type ApiNotification,
  deleteNotification,
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/src/infrastructure/lib/api';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

type NotificationTarget =
  | { kind: 'route'; path: string }
  | { kind: 'external'; href: string }
  | { kind: 'modal' };

/**
 * Decide where clicking an admin notification should take the operator. Each
 * available admin route (feedback, support ticket, user, invitation) is mapped
 * from the metadata ID the backend already persists with the notification.
 */
function resolveTarget(n: ApiNotification): NotificationTarget {
  const meta = (n.metadata ?? {}) as Record<string, unknown>;
  const feedbackId = typeof meta.feedbackId === 'string' ? meta.feedbackId : null;
  const ticketId = typeof meta.ticketId === 'string' ? meta.ticketId : null;
  const userId = typeof meta.userId === 'string' ? meta.userId : null;
  const invitationId = typeof meta.invitationId === 'string' ? meta.invitationId : null;
  const ctaUrl = typeof meta.ctaUrl === 'string' ? meta.ctaUrl : null;

  if (feedbackId) return { kind: 'route', path: `/feedback/${feedbackId}` };
  if (ticketId) return { kind: 'route', path: `/support/${ticketId}` };
  if (userId) return { kind: 'route', path: `/users/${userId}` };
  if (invitationId) return { kind: 'route', path: `/invitations?highlight=${invitationId}` };
  if (n.category === 'MARKETING' && ctaUrl) return { kind: 'external', href: ctaUrl };
  return { kind: 'modal' };
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ApiNotification | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(() => {
    getUnreadNotificationCount()
      .then((r) => setUnread(r.unreadCount))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshCount();
    const id = window.setInterval(refreshCount, 60_000);
    return () => window.clearInterval(id);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: globalThis.MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getNotifications({ limit: 10 })
      .then((page) => {
        setItems(page.items);
        setUnread(page.unreadCount);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [open]);

  async function markReadLocal(n: ApiNotification) {
    if (n.isRead) return;
    setItems((list) => list.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
    setUnread((c) => Math.max(0, c - 1));
    await markNotificationRead(n.id).catch(() => undefined);
  }

  async function handleOpen(n: ApiNotification) {
    await markReadLocal(n);
    const target = resolveTarget(n);
    if (target.kind === 'route') {
      setOpen(false);
      navigate(target.path);
    } else if (target.kind === 'external') {
      setOpen(false);
      window.open(target.href, '_blank', 'noopener,noreferrer');
    } else {
      setDetail({ ...n, isRead: true });
      setOpen(false);
    }
  }

  async function handleMarkAll() {
    setItems((list) => list.map((x) => ({ ...x, isRead: true })));
    setUnread(0);
    await markAllNotificationsRead().catch(() => undefined);
  }

  async function handleDelete(id: string, e: MouseEvent) {
    e.stopPropagation();
    const removed = items.find((x) => x.id === id);
    setItems((list) => list.filter((x) => x.id !== id));
    if (removed && !removed.isRead) setUnread((c) => Math.max(0, c - 1));
    await deleteNotification(id).catch(() => undefined);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        className="relative text-on-surface-variant hover:text-primary transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-electric-violet text-white border-2 border-surface">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[90vw] bg-surface rounded-2xl border border-border-subtle shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <span className="text-sm font-bold text-on-surface">Notifications</span>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-xs font-medium text-primary hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <p className="px-4 py-8 text-center text-sm text-on-surface-variant">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-on-surface-variant">
                You're all caught up.
              </p>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleOpen(n)}
                  className={`group flex gap-3 px-4 py-3 cursor-pointer border-b border-border-subtle last:border-0 transition-colors hover:bg-surface-container-low ${
                    n.isRead ? '' : 'bg-primary/[0.04]'
                  }`}
                >
                  <span
                    className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${
                      n.isRead ? 'bg-transparent' : 'bg-electric-violet'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-on-surface truncate">{n.title}</p>
                      <span className="text-[11px] text-on-surface-variant shrink-0">
                        {timeAgo(n.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-on-surface-variant mt-0.5 line-clamp-2">
                      {n.message}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDelete(n.id, e)}
                    aria-label="Delete notification"
                    className="opacity-0 group-hover:opacity-100 transition-opacity self-center text-on-surface-variant hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {detail && <NotificationDetailModal n={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function NotificationDetailModal({
  n,
  onClose,
}: {
  n: ApiNotification;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface rounded-2xl border border-border-subtle shadow-xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-subtle">
          <div className="min-w-0">
            <p className="text-base font-bold text-on-surface">{n.title}</p>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              {new Date(n.createdAt).toLocaleString()} · {n.category}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-on-surface-variant hover:text-on-surface"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-on-surface whitespace-pre-wrap">{n.message}</p>
        </div>
      </div>
    </div>
  );
}
